import { renderAsync } from 'docx-preview';
import { useRef, useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';

import { WordLayoutPart, WordPlaceholderChanges } from 'src/services/presentations/presentations.types';

import { decorateDocument, mapRenderedDocument } from './word-document-view.utils';

// ----------------------------------------------------------------------

type Props = {
  /** The Word file's bytes. A Blob, not a URL: see guest-docx-preview.tsx on the CSP and `blob:` URLs. */
  blob: Blob;
  parts: WordLayoutPart[];
  changes: WordPlaceholderChanges;
  repeatLabel: (list: string) => string;
  /** docx-preview couldn't draw the file: the editor shows its text view instead. */
  onFailed: VoidFunction;
};

/** How long after a change the page is drawn again, so several quick changes draw once. */
const REDRAW_DELAY_MS = 150;

/**
 * The agency's Word file as it looks, with headers, footers, images, tables and colours, for the
 * placeholder editor (staging feedback, JT1-1887). The page is drawn again from the same bytes after
 * each change, with the pending placeholders and repeats drawn on; the file itself only changes when
 * the admin applies them. Drawn off-screen first, so the page doesn't flicker while it redraws.
 */
export default function WordDocumentView({ blob, parts, changes, repeatLabel, onFailed }: Props) {
  const container = useRef<HTMLDivElement>(null);
  const drawn = useRef(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const node = container.current;
    if (!node) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const page = node.ownerDocument.createElement('div');
        await renderAsync(blob, page, undefined, {
          inWrapper: true,
          breakPages: true,
          ignoreWidth: false,
          ignoreHeight: true,
          renderHeaders: true,
          renderFooters: true,
          experimental: true,
          useBase64URL: true,
        });
        if (cancelled) return;
        mapRenderedDocument(page, parts);
        decorateDocument(page, parts, changes, repeatLabel);
        node.replaceChildren(...Array.from(page.childNodes));
        drawn.current = true;
        setReady(true);
      } catch {
        if (!cancelled) onFailed();
      }
    }, drawn.current ? REDRAW_DELAY_MS : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // repeatLabel and onFailed are the editor's; a change to them doesn't need a redraw.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blob, parts, changes]);

  return (
    <>
      {!ready && <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress /></Stack>}
      <Box
        ref={container}
        sx={{
          display: ready ? 'block' : 'none',
          overflowX: 'auto',
          cursor: 'text',
          '& .docx-wrapper': { background: 'transparent', padding: 0, alignItems: 'center' },
          '& .docx-wrapper > section.docx': { boxShadow: 1, marginBottom: 3 },
          [`& .jt-placeholder`]: { px: 0.5, borderRadius: 0.5, bgcolor: 'primary.lighter', color: 'primary.darker', fontFamily: 'monospace', fontSize: '0.92em' },
          '& .jt-repeat': { boxShadow: (theme) => `inset 3px 0 0 ${theme.palette.primary.main}`, bgcolor: 'primary.lighter' },
          '& .jt-repeat-row > td': { bgcolor: 'primary.lighter' },
          '& .jt-taken-out': { display: 'none' },
          '& .jt-repeat-label': { display: 'inline-block', my: 0.5, px: 1, borderRadius: 1, bgcolor: 'primary.main', color: 'primary.contrastText', fontSize: 12, fontFamily: 'inherit', userSelect: 'none' },
        }}
      />
    </>
  );
}
